
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, Download, Archive, X } from 'lucide-react';
import { useAttachments } from '@/context/AttachmentContext';
import { cn } from '@/lib/utils';

const DownloadManager: React.FC = () => {
  const { selected, filteredAttachments, downloadSelected } = useAttachments();
  const [isDownloading, setIsDownloading] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  
  // Only show when items are selected
  if (selected.size === 0) {
    return null;
  }
  
  // Calculate size of selected files
  const selectedAttachments = filteredAttachments.filter(att => selected.has(att.id));
  const totalSize = selectedAttachments.reduce((acc, att) => acc + att.fileSize, 0);
  
  // Format total size
  const formatTotalSize = (bytes: number): string => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  
  const handleDownload = async () => {
    setIsDownloading(true);
    setShowBanner(true);
    
    try {
      await downloadSelected();
    } finally {
      setIsDownloading(false);
      
      // Auto-hide banner after download completes
      setTimeout(() => {
        setShowBanner(false);
      }, 3000);
    }
  };
  
  return (
    <div
      className={cn(
        "fixed bottom-0 left-0 right-0 bg-background border-t border-border transition-transform duration-300 transform shadow-lg",
        showBanner ? "translate-y-0" : "translate-y-full"
      )}
    >
      <div className="container-pad py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Archive className="h-5 w-5 text-primary mr-3" />
            <div>
              <h4 className="font-medium">
                {selected.size} file{selected.size !== 1 ? 's' : ''} selected
              </h4>
              <p className="text-sm text-muted-foreground">
                Total size: {formatTotalSize(totalSize)}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setShowBanner(false)}
              className="h-8 px-2"
            >
              <X className="h-4 w-4" />
            </Button>
            
            <Button
              onClick={handleDownload}
              disabled={isDownloading}
              className="relative overflow-hidden h-9"
            >
              {isDownloading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Downloading...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Download as ZIP
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DownloadManager;
